import { modalityService } from '@/services/modalityService'
import { Modality } from '@/types/modality'

interface CachedModalities {
  data: Modality[]
  timestamp: number
}

class ModalitiesManager {
  private static instance: ModalitiesManager
  private modalities: Modality[] = []
  private modalitiesMap: Map<string, Modality> = new Map()
  private modalitiesLoaded = false
  private loadingPromise: Promise<Modality[]> | null = null
  private readonly CACHE_DURATION = 24 * 60 * 60 * 1000 // 24 horas em millisegundos
  private readonly CACHE_KEY = 'meiuca-app-modalities'

  constructor() {
    this.loadFromLocalStorage()
  }

  static getInstance(): ModalitiesManager {
    if (!ModalitiesManager.instance) {
      ModalitiesManager.instance = new ModalitiesManager()
    }
    return ModalitiesManager.instance
  }

  private loadFromLocalStorage(): void {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY)
      if (cached) {
        const cachedData: CachedModalities = JSON.parse(cached)
        if (this.isCacheValidFromTimestamp(cachedData.timestamp)) {
          this.modalities = cachedData.data
          this.buildModalitiesMap()
          this.modalitiesLoaded = true
        } else {
          // Cache expirado, remove do localStorage
          localStorage.removeItem(this.CACHE_KEY)
        }
      }
    } catch (error) {
      console.warn('[MEIUCA-APP] Erro ao carregar cache do localStorage:', error)
      localStorage.removeItem(this.CACHE_KEY)
    }
  }

  private saveToLocalStorage(): void {
    try {
      const cacheData: CachedModalities = {
        data: this.modalities,
        timestamp: Date.now()
      }
      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheData))
    } catch (error) {
      console.warn('[MEIUCA-APP] Erro ao salvar cache no localStorage:', error)
    }
  }

  private isCacheValidFromTimestamp(timestamp: number): boolean {
    return (Date.now() - timestamp) < this.CACHE_DURATION
  }

  private isCacheValid(): boolean {
    return this.modalitiesLoaded
  }

  private buildModalitiesMap(): void {
    this.modalitiesMap.clear()
    this.modalities.forEach(modality => {
      this.modalitiesMap.set(modality.id, modality)
    })
  }

  async loadModalities(): Promise<Modality[]> {
    // Se já existe uma requisição em andamento, aguarda ela
    if (this.loadingPromise) {
      return this.loadingPromise
    }

    // Se o cache ainda é válido, retorna os dados em cache
    if (this.isCacheValid()) {
      return this.modalities
    }

    // Cria uma nova promessa para carregar as modalidades
    this.loadingPromise = this.fetchModalitiesFromAPI()

    try {
      const modalities = await this.loadingPromise
      this.modalities = modalities
      this.buildModalitiesMap()
      this.modalitiesLoaded = true
      this.saveToLocalStorage()
      return modalities
    } catch (error) {
      console.error('[MEIUCA-APP] Erro ao carregar modalidades:', error)
      throw error
    } finally {
      this.loadingPromise = null
    }
  }

  private async fetchModalitiesFromAPI(): Promise<Modality[]> {
    return modalityService.getModalities()
  }

  async getModalities(): Promise<Modality[]> {
    return this.loadModalities()
  }

  getModalitiesSync(): Modality[] {
    if (!this.modalitiesLoaded) {
      console.warn('[MEIUCA-APP] Modalidades não foram carregadas ainda. Use loadModalities() primeiro.')
      return []
    }
    return this.modalities
  }

  getModalityById(id: string): Modality | undefined {
    return this.modalitiesMap.get(id)
  }

  getModalityDescription(id: string): string {
    const modality = this.getModalityById(id)
    return modality?.description || id
  }

  getModalitiesMap(): Map<string, Modality> {
    return this.modalitiesMap
  }

  getModalitiesDescriptions(ids: string[]): string[] {
    return ids.map(id => this.getModalityDescription(id))
  }

  isLoaded(): boolean {
    return this.modalitiesLoaded
  }

  clearCache(): void {
    this.modalities = []
    this.modalitiesMap.clear()
    this.modalitiesLoaded = false
    localStorage.removeItem(this.CACHE_KEY)
  }

  refreshModalities(): Promise<Modality[]> {
    this.clearCache()
    return this.loadModalities()
  }
}

// Exporta a instância singleton
export const modalitiesManager = ModalitiesManager.getInstance()
export default modalitiesManager
